import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { Product, ProductAttributes } from '../../types';
import { productService } from '../../services/productService';
import { attributeService } from '../../services/attributeService';
import BarcodePrintModal from '../../components/BarcodePrintModal';
import './ProductCategoryDetailPage.css';
import { ArrowLeft, Barcode, PenLine, Trash2, Package, ShoppingCart, ShoppingBag } from 'lucide-react';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [attributes, setAttributes] = useState<ProductAttributes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);

  useEffect(() => {
    loadProduct();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      const productId = parseInt(id || '0');
      const [response, attrsRes] = await Promise.all([
        productService.getProductById(productId),
        attributeService.getProductAttributes(productId).catch(() => null),
      ]);
      if (response.data.data) {
        setProduct(response.data.data as Product);
      }
      if (attrsRes?.data?.data) {
        setAttributes(attrsRes.data.data as ProductAttributes);
      }
      setLoading(false);
    } catch {
      setError(t('product.failedToLoadProduct'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('product.productNotFound')}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/products')}>
            {t('product.backToProducts')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button className="detail-breadcrumb-btn" onClick={() => navigate('/products')}>
            <ArrowLeft size={16} strokeWidth={2} />
            {t('product.backToProducts')}
          </button>
        </nav>

        <div className="detail-header">
          <div>
            <h1 className="detail-title">{product.name}</h1>
            <div className="detail-meta">
              <span className="detail-meta-item">
                <code>{product.sku}</code>
              </span>
              <span className="detail-meta-item">
                <span className={`badge ${product.isDeleted ? 'badge-inactive' : 'badge-active'}`}>
                  {product.isDeleted ? t('product.deleted') : t('product.active')}
                </span>
              </span>
            </div>
          </div>
          <div>
            <button
              type="button"
              className="ims-btn ims-btn-secondary"
              onClick={() => setShowBarcodeModal(true)}
            >
              <Barcode size={15} strokeWidth={2} />
              {t('barcode.printBarcodes')}
            </button>
          </div>
        </div>

        {showBarcodeModal && (
          <BarcodePrintModal
            isOpen
            productId={product.id}
            productName={product.name}
            onClose={() => setShowBarcodeModal(false)}
          />
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('product.details')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('product.sku')}</span>
                    <span className="detail-value"><code>{product.sku}</code></span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.category')}</span>
                    <span className="detail-value">{product.categoryName || 'N/A'}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.unitOfMeasure')}</span>
                    <span className="detail-value">{product.unitOfMeasure}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.reorderLevel')}</span>
                    <span className="detail-value">{product.reorderLevel} {t('product.units')}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.reorderQuantity')}</span>
                    <span className="detail-value">{product.reorderQuantity} {t('product.units')}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.status')}</span>
                    <span className="detail-value">
                      <span className={`badge ${product.isDeleted ? 'badge-inactive' : 'badge-active'}`}>
                        {product.isDeleted ? t('product.deleted') : t('product.active')}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="detail-field detail-field-full">
                  <span className="detail-label">{t('product.description')}</span>
                  <span className="detail-value">
                    {product.description || t('product.noDescriptionProvided')}
                  </span>
                </div>
              </div>
            </div>

            {attributes && attributes.moduleAttributes.length > 0 && (
              <div className="detail-card">
                <div className="detail-card-header">
                  <h2 className="detail-card-title">
                    {attributes.moduleSetName || t('attribute.moduleSection')}
                  </h2>
                </div>
                <div className="detail-card-body">
                  <div className="detail-grid">
                    {attributes.moduleAttributes.map((attr) => (
                      <div className="detail-field" key={attr.attributeDefinitionId}>
                        <span className="detail-label">{attr.name}</span>
                        <span className="detail-value">
                          {attr.value != null && attr.value !== ''
                            ? `${attr.dataType === 'Boolean'
                                ? (attr.value === 'true' ? t('common.yes') : t('common.no'))
                                : attr.value}${attr.unit ? ` ${attr.unit}` : ''}`
                            : t('attribute.notSet')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Custom attributes — only the ones that actually hold a value */}
            {attributes && attributes.customAttributes.some((a) => a.value != null && a.value !== '') && (
              <div className="detail-card">
                <div className="detail-card-header">
                  <h2 className="detail-card-title">{t('attribute.title')}</h2>
                </div>
                <div className="detail-card-body">
                  <div className="detail-grid">
                    {attributes.customAttributes
                      .filter((attr) => attr.value != null && attr.value !== '')
                      .map((attr) => (
                        <div className="detail-field" key={attr.attributeDefinitionId}>
                          <span className="detail-label">{attr.name}</span>
                          <span className="detail-value">
                            {attr.dataType === 'Boolean'
                              ? (attr.value === 'true' ? t('common.yes') : t('common.no'))
                              : attr.value}{attr.unit ? ` ${attr.unit}` : ''}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('product.auditInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('product.productId')}</span>
                    <span className="detail-value">{product.id}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.createdDate')}</span>
                    <span className="detail-value">
                      {new Date(product.createdAt).toLocaleDateString(langCode)}{' '}
                      {new Date(product.createdAt).toLocaleTimeString(langCode)}
                    </span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('product.lastModified')}</span>
                    <span className="detail-value">
                      {product.modifiedAt
                        ? `${new Date(product.modifiedAt).toLocaleDateString(langCode)} ${new Date(product.modifiedAt).toLocaleTimeString(langCode)}`
                        : t('product.never')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('product.actions')}</h3>
              </div>
              <div className="detail-card-body">
                <div className="detail-actions">
                  <button
                    className="btn-save"
                    onClick={() => navigate(`/products/${product.id}/edit`)}
                  >
                    <PenLine size={16} strokeWidth={2} />
                    {t('product.editProduct')}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => navigate(`/products/${product.id}/delete`)}
                  >
                    <Trash2 size={16} strokeWidth={2} />
                    {t('product.deleteProduct')}
                  </button>
                  <button
                    className="detail-btn detail-btn-secondary"
                    onClick={() => navigate('/products')}
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                    {t('product.backToProducts')}
                  </button>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('product.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/stock/levels')}
                >
                  <Package size={16} strokeWidth={2} />
                  {t('product.viewStock')}
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/purchase-items?productId=${product.id}`)}
                  title={`Purchase items for ${product.name}`}
                >
                  <ShoppingCart size={16} strokeWidth={2} />
                  Purchase Items
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/sales-items?productId=${product.id}`)}
                  title={`Sales items for ${product.name}`}
                >
                  <ShoppingBag size={16} strokeWidth={2} />
                  Sales Items
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/purchase-orders')}
                >
                  <Package size={16} strokeWidth={2} />
                  {t('product.viewOrders')}
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/stock/movements')}
                >
                  <ArrowLeft size={16} strokeWidth={2} style={{ transform: 'rotate(180deg)' }} />
                  {t('product.viewStockMovements')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;
